import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  LinearProgress,
  Paper,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import ShowChartRoundedIcon from '@mui/icons-material/ShowChartRounded';
import TimelineRoundedIcon from '@mui/icons-material/TimelineRounded';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Sparklines, SparklinesLine } from 'react-sparklines';
import { getPatternTitle } from './patternTutorialData';
import {
  fetchAssetMetricHistory,
  fetchAssetMetricSnapshot,
  fetchPatternImageAsBlob,
  recordPatternScannerPageView,
  fetchScannerDetail,
} from './scannerExperienceApi';
import {
  DEFAULT_PATTERN_ENGINE_VERSION,
  NO_CLEAR_PATTERN_KEY,
  SCANNER_DETAIL_TIMEFRAMES,
  DEFAULT_TRADE_ENGINE_VERSION,
  clampSummaryText,
  buildValidationMatrix,
  formatConfidence,
  formatDateTime,
  formatHistoryDate,
  formatPercent,
  formatPrice,
  formatProbability,
  formatRatio,
  formatRiskReward,
  normalizeText,
  resolveAlignmentDescriptor,
  resolveConvictionLabel,
  resolveConvictionScore,
  resolveDisplayedPatternKey,
  resolveDirectionDescriptor,
  resolveGeometryTypeLabel,
  resolvePatternEngineV2Metadata,
  resolveRiskDescriptor,
  resolveTradeDirectionDescriptor,
  resolveVolumeDescriptor,
} from './scannerExperienceShared';

function formatGeometryFlag(flag) {
  switch (flag) {
    case 'LOW_STRUCTURE_QUALITY':
      return 'Qualité structurelle trop faible';
    case 'LOW_GEOMETRY_CONFIDENCE':
      return 'Confiance géométrique faible';
    case 'TOO_FEW_CONTACTS':
      return 'Trop peu de contacts validés';
    case 'EXCESSIVE_NOISE':
      return 'Bruit excessif sur la structure';
    case 'AMBIGUOUS_STRUCTURE':
      return 'Structure ambiguë';
    default:
      return normalizeText(flag, 'Signal géométrique');
  }
}

function formatStructureBiasLabel(value) {
  switch (String(value || '').toUpperCase()) {
    case 'BULLISH':
      return 'Haussière';
    case 'BEARISH':
      return 'Baissière';
    default:
      return 'Neutre';
  }
}

function formatPatternFamilyLabel(value) {
  switch (String(value || '').toUpperCase()) {
    case 'DISTRIBUTION':
      return 'Distribution';
    case 'ACCUMULATION':
      return 'Accumulation';
    case 'COMPRESSION':
      return 'Compression';
    case 'TREND_CONTINUATION':
      return 'Tendance / continuation';
    case 'RANGE_EXPANSION':
      return 'Expansion de range';
    case 'REVERSAL':
      return 'Réversion';
    case 'RANGE':
      return 'Range';
    default:
      return normalizeText(value, 'n/a');
  }
}

function formatEventCount(value, suffix = '') {
  return Number.isFinite(Number(value)) ? `${Math.round(Number(value))}${suffix}` : 'n/a';
}

function formatSwingAtr(value) {
  return Number.isFinite(Number(value)) ? `${Number(value).toFixed(2)} ATR` : 'n/a';
}

function buildStructureEventItems(events) {
  if (!events) {
    return [];
  }
  return [
    ['Résistance testée', formatEventCount(events.resistanceTests, ' fois')],
    ['Support testé', formatEventCount(events.supportTests, ' fois')],
    ['Tentatives de cassure', formatEventCount(events.breakoutAttempts)],
    ['Breakouts échoués', formatEventCount(events.failedBreakouts)],
    ['Rejets supérieurs', formatEventCount(events.upperRejections)],
    ['Rejets inférieurs', formatEventCount(events.lowerRejections)],
    ['Neckline', events.necklineDetected ? 'Oui' : 'Non'],
    ['Volume spike', events.volumeSpike ? 'Oui' : 'Non'],
    ['Durée structurelle', formatEventCount(events.structureDuration, ' bougies')],
  ].filter(([, value]) => value !== 'n/a');
}

function buildConfidenceItems(confidenceBreakdown) {
  if (!confidenceBreakdown) {
    return [];
  }
  return [
    ['Confiance structurelle', Number.isFinite(Number(confidenceBreakdown.structureConfidence)) ? `${Math.round(Number(confidenceBreakdown.structureConfidence))}%` : 'n/a'],
    ['Qualité structurelle', Number.isFinite(Number(confidenceBreakdown.structureQuality)) ? `${Math.round(Number(confidenceBreakdown.structureQuality))}%` : 'n/a'],
    ['Confiance pattern', Number.isFinite(Number(confidenceBreakdown.patternConfidence)) ? `${Math.round(Number(confidenceBreakdown.patternConfidence))}%` : 'n/a'],
    ['Confiance trade', Number.isFinite(Number(confidenceBreakdown.tradeConfidence)) ? `${Math.round(Number(confidenceBreakdown.tradeConfidence))}%` : 'n/a'],
    ['Écart avec l’alternative', Number.isFinite(Number(confidenceBreakdown.candidateSpread)) ? `${Math.round(Number(confidenceBreakdown.candidateSpread))} pts` : 'n/a'],
    ['Ambiguïté', normalizeText(confidenceBreakdown.ambiguityLabel, 'n/a')],
  ].filter(([, value]) => value !== 'n/a');
}

function buildSwingSummary(swings) {
  if (!Array.isArray(swings) || swings.length === 0) {
    return [];
  }
  return swings.map((swing) => {
    const label = normalizeText(swing.label, swing.type === 'HIGH' ? 'H?' : 'L?');
    return `${label} · ${formatPrice(Number(swing.price))} · importance ${Number.isFinite(Number(swing.importance)) ? `${Math.round(Number(swing.importance) * 100)}%` : 'n/a'} · ${formatSwingAtr(swing.relativeAtr)}`;
  });
}

function normalizeEngineVersion(value, fallback) {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) && numericValue >= 1 ? numericValue : fallback;
}

function buildDetailSections(screen) {
  const pattern = screen?.pattern || {};
  const metadata = resolvePatternEngineV2Metadata(pattern);
  const geometry = metadata.geometry;
  const structure = metadata.structure;
  const events = metadata.events;
  const confidenceBreakdown = metadata.confidenceBreakdown;
  const displayedPatternKey = resolveDisplayedPatternKey(pattern) || pattern.patternDetected || '';
  return [
    {
      title: 'Structure',
      items: [
        ...(structure ? [['Lecture structurelle', normalizeText(structure.label, 'n/a')]] : []),
        ...(structure ? [['Famille', formatPatternFamilyLabel(structure.patternFamilyLabel || structure.patternFamilyCode)]] : []),
        ['Pattern', getPatternTitle(displayedPatternKey)],
        ['Biais', resolveDirectionDescriptor(pattern.direction).label],
        ['Confiance', formatConfidence(pattern.confidence)],
        ...(geometry ? [['Géométrie', resolveGeometryTypeLabel(geometry.type)]] : []),
        ...(confidenceBreakdown ? [['Confiance structurelle', Number.isFinite(Number(confidenceBreakdown.structureConfidence)) ? `${Math.round(Number(confidenceBreakdown.structureConfidence))}%` : 'n/a']] : []),
        ['Résolution', normalizeText(pattern.timeToResolution, 'n/a')],
      ],
    },
    ...(events ? [{
      title: 'Événements',
      items: buildStructureEventItems(events),
    }] : []),
    {
      title: 'Volume',
      items: [
        ['État', resolveVolumeDescriptor(pattern).label],
        ['Commentaire', normalizeText(pattern.volumeComment, 'n/a')],
        ['Ratio breakout', Number.isFinite(pattern.breakoutVolumeRatio) ? pattern.breakoutVolumeRatio.toFixed(2) : 'n/a'],
      ],
    },
    {
      title: 'Momentum',
      items: [
        ['État', normalizeText(pattern.momentum?.state || pattern.momentumState, 'n/a')],
        ['Force', Number.isFinite(pattern.momentum?.strength) ? `${Math.round(pattern.momentum.strength * 100)}%` : 'n/a'],
        ['Commentaire', normalizeText(pattern.momentum?.comment, 'n/a')],
      ],
    },
    {
      title: 'Divergence',
      items: [
        ['Type', normalizeText(pattern.divergence?.type || pattern.divergenceType, 'n/a')],
        ['Force', Number.isFinite(pattern.divergence?.strength) ? `${Math.round(pattern.divergence.strength * 100)}%` : 'n/a'],
        ['Commentaire', normalizeText(pattern.divergence?.comment, 'n/a')],
      ],
    },
    {
      title: 'Convergence',
      items: [
        ['État fusion', normalizeText(pattern.signalFusion?.state || pattern.signalFusionState, 'n/a')],
        ['Conviction', String(resolveConvictionScore(screen))],
        ['Narrative IA', normalizeText(pattern.signalFusion?.aiNarrative, normalizeText(pattern.aiNarrative, 'n/a'))],
      ],
    },
    {
      title: 'Candlestick',
      items: [
        ['Pattern', normalizeText(pattern.candlestick?.pattern || pattern.candlestickPattern, 'n/a')],
        ['Direction', normalizeText(pattern.candlestick?.direction, 'n/a')],
        ['Commentaire', normalizeText(pattern.candlestick?.comment, 'n/a')],
      ],
    },
  ];
}

function SummaryTile({ label, value, helper, tone, loading = false }) {
  return (
    <Paper elevation={0} sx={{ p: 1.8, borderRadius: 3.5, background: '#fffdf8', border: '1px solid rgba(16,37,43,0.08)' }}>
      <Typography variant="overline" sx={{ color: '#68777d', letterSpacing: '0.08em' }}>{label}</Typography>
      {loading ? (
        <Stack spacing={0.6} sx={{ mt: 0.5 }}>
          <Skeleton variant="text" animation="wave" width="68%" height={40} />
          <Skeleton variant="text" animation="wave" width="92%" height={22} />
        </Stack>
      ) : (
        <>
          <Typography variant="h5" sx={{ color: tone, fontWeight: 900, mb: 0.4 }}>{value}</Typography>
          <Typography variant="body2" sx={{ color: '#5c6d74', lineHeight: 1.65 }}>{helper}</Typography>
        </>
      )}
    </Paper>
  );
}

function SectionLoadingNotice({ message }) {
  return (
    <Alert severity="info" variant="outlined" sx={{ borderRadius: 2.8 }}>
      {message}
    </Alert>
  );
}

function buildExecutionPlan(pattern, options = {}) {
  const baseItems = [
    {
      label: 'Cassure',
      value: formatPrice(pattern?.breakoutLevel),
      helper: 'Niveau à valider pour activer le setup.',
    },
    {
      label: 'Cible',
      value: formatPrice(pattern?.targetPrice),
      helper: 'Projection technique du mouvement.',
    },
    {
      label: 'Invalidation',
      value: formatPrice(pattern?.stopInvalidation),
      helper: 'Zone où le scénario perd sa validité.',
    },
    {
      label: 'R/R',
      value: formatRatio(pattern?.riskRewardRatio),
      helper: 'Ratio rendement / risque du plan.',
    },
    {
      label: 'Confiance cible',
      value: formatPercent(pattern?.targetConfidence),
      helper: 'Probabilité de rejoindre la projection.',
    },
    {
      label: 'Résolution',
      value: normalizeText(pattern?.timeToResolution, 'n/a'),
      helper: 'Horizon tactique estimé.',
    },
  ];

  if (!options.isV2) {
    return baseItems;
  }

  return [
    ...baseItems,
    {
      label: 'Pattern',
      value: normalizeText(options.displayedPatternTitle, 'n/a'),
      helper: 'Figure retenue par la lecture v2.',
    },
    {
      label: 'Confiance pattern',
      value: formatConfidence(pattern?.confidence),
      helper: 'Confiance du pattern actuellement affiché.',
    },
    {
      label: 'Probabilité haussière',
      value: Number.isFinite(Number(pattern?.pumpProbability)) ? `${Math.round(Number(pattern.pumpProbability))}%` : 'n/a',
      helper: 'Projection haussière estimée par le moteur.',
    },
    {
      label: 'Probabilité baissière',
      value: Number.isFinite(Number(pattern?.dumpProbability)) ? `${Math.round(Number(pattern.dumpProbability))}%` : 'n/a',
      helper: 'Projection baissière concurrente du scénario.',
    },
    {
      label: 'Risque fakeout',
      value: Number.isFinite(Number(pattern?.fakeoutRisk)) ? `${Math.round(Number(pattern.fakeoutRisk))}%` : 'n/a',
      helper: 'Risque de faux signal sur la configuration.',
    },
    {
      label: 'Volume confirmé',
      value: pattern ? (pattern.volumeConfirmation ? 'Oui' : 'Non') : 'n/a',
      helper: 'Validation volume sur le scénario v2 affiché.',
    },
  ];
}

function buildTradePlanHighlights(tradePlan) {
  if (!tradePlan) {
    return [];
  }

  return [
    {
      label: 'Trade Grade',
      value: normalizeText(tradePlan.tradeGrade, 'n/a'),
      helper: normalizeText(tradePlan.gradeComment, 'Qualité du setup selon structure, volume, momentum et contexte MTF.'),
    },
    {
      label: 'Conviction',
      value: Number.isFinite(tradePlan.conviction) ? `${tradePlan.conviction}` : 'n/a',
      helper: normalizeText(tradePlan.displayEligible ? 'Le scénario dépasse le seuil d’activation.' : tradePlan.displayMessage, 'Lecture du moteur indisponible.'),
    },
    {
      label: 'RR estimé',
      value: formatRiskReward(tradePlan.riskReward),
      helper: 'Ratio calculé sur l’entrée médiane, le stop robuste et le TP principal.',
    },
    {
      label: 'Probabilité',
      value: formatProbability(tradePlan.probability),
      helper: 'Lecture probabiliste du scénario principal, jamais une certitude.',
    },
  ];
}

function buildTradePlanReasoning(tradePlan) {
  const reasoning = tradePlan?.reasoning || {};
  return [
    ['Pourquoi cette direction ?', normalizeText(reasoning.whyDirection, 'n/a')],
    ['Pourquoi ce stop ?', normalizeText(reasoning.whyStop, 'n/a')],
    ['Pourquoi ces objectifs ?', normalizeText(reasoning.whyTargets, 'n/a')],
    ['Pourquoi ce levier ?', normalizeText(reasoning.whyLeverage, 'n/a')],
    ['Pourquoi ce RR ?', normalizeText(reasoning.whyRiskReward, 'n/a')],
  ];
}

function formatAssetMetric(value, options = {}) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return 'n/a';
  }
  return new Intl.NumberFormat('fr-FR', options).format(numericValue);
}

function formatAssetSignedPercent(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return 'n/a';
  }
  const prefix = numericValue > 0 ? '+' : '';
  return `${prefix}${numericValue.toFixed(1)}%`;
}

function formatAssetVolume(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return 'n/a';
  }
  return new Intl.NumberFormat('fr-FR', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(numericValue);
}

function resolveAssetScoreDescriptor(value) {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) {
    return { label: 'n/a', tone: '#5f6f75', background: 'rgba(95,111,117,0.12)' };
  }
  if (numericValue >= 80) {
    return { label: 'Prioritaire', tone: '#1f7a4d', background: 'rgba(31,122,77,0.12)' };
  }
  if (numericValue >= 60) {
    return { label: 'Constructif', tone: '#214f77', background: 'rgba(33,79,119,0.12)' };
  }
  if (numericValue >= 40) {
    return { label: 'Sous surveillance', tone: '#8d6c16', background: 'rgba(141,108,22,0.14)' };
  }
  return { label: 'Fragile', tone: '#aa3d33', background: 'rgba(170,61,51,0.14)' };
}

function buildAssetScannerNarrative(asset) {
  if (!asset) {
    return 'Le snapshot Asset Scanner est indisponible pour cet actif.';
  }

  const score = Number(asset.compositeScore);
  const momentum = Number(asset.momentumScore);
  const volatility = Number(asset.volatility);
  const pattern = getPatternTitle(asset.detectedPattern || '');

  const lead = Number.isFinite(score) && score >= 80
    ? `Zone prioritaire du scanner asset avec un score composite de ${Math.round(score)}.`
    : Number.isFinite(score) && score >= 60
      ? `Lecture constructive mais encore sélective avec un score composite de ${Math.round(score)}.`
      : `Actif encore secondaire dans le scanner asset avec un score composite de ${Number.isFinite(score) ? Math.round(score) : 'n/a'}.`;

  const momentumSentence = Number.isFinite(momentum)
    ? `Momentum ${Math.round(momentum)} et volatilité ${Number.isFinite(volatility) ? Math.round(volatility) : 'n/a'}/10 sur le dernier snapshot.`
    : 'Le snapshot ne remonte pas encore de momentum exploitable.';

  const patternSentence = asset.detectedPattern
    ? `Le scanner asset conserve ${pattern.toLowerCase()} comme structure dominante.`
    : 'Aucun pattern dominant n’est encore priorisé par le scanner asset.';

  return `${lead} ${momentumSentence} ${patternSentence}`;
}

function buildAssetHistoryWindow(history) {
  if (!Array.isArray(history)) {
    return [];
  }
  return history
    .filter((entry) => entry && entry.timestamp)
    .slice(0, 12)
    .reverse();
}

function extractNumericSeries(historyWindow, field) {
  return historyWindow
    .map((entry) => Number(entry?.[field]))
    .filter((value) => Number.isFinite(value));
}

const DETAIL_PAGE_TABS = [
  { value: 'pattern', label: 'Pattern' },
  { value: 'trade-plan', label: 'Trade Plan' },
  { value: 'asset-scanner', label: 'Asset Scanner' },
  { value: 'analysis', label: 'Analyse détaillée' },
  { value: 'history', label: 'Historique Pattern' },
];

const DEFAULT_DETAIL_TAB = 'pattern';

function normalizeDetailTab(value) {
  return DETAIL_PAGE_TABS.some((tab) => tab.value === value) ? value : DEFAULT_DETAIL_TAB;
}

function DetailTabPanel({ value, activeValue, children }) {
  if (value !== activeValue) {
    return null;
  }

  return (
    <Box
      role="tabpanel"
      id={`scanner-detail-tabpanel-${value}`}
      aria-labelledby={`scanner-detail-tab-${value}`}
      sx={{ width: '100%' }}
    >
      {children}
    </Box>
  );
}

const PatternScannerDetailPage = ({ user = null, authResolved = true }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { symbol } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const exchange = searchParams.get('exchange') || 'BINANCE';
  const timeframe = searchParams.get('timeframe') || '4H';
  const patternEngineVersion = normalizeEngineVersion(searchParams.get('patternEngineVersion'), DEFAULT_PATTERN_ENGINE_VERSION);
  const tradeEngineVersion = normalizeEngineVersion(searchParams.get('tradeEngineVersion'), DEFAULT_TRADE_ENGINE_VERSION);
  const [screen, setScreen] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [imageLoading, setImageLoading] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  const [assetScannerSnapshot, setAssetScannerSnapshot] = useState(null);
  const [assetScannerHistory, setAssetScannerHistory] = useState([]);
  const [assetScannerLoading, setAssetScannerLoading] = useState(true);
  const [assetScannerError, setAssetScannerError] = useState('');
  const activeDetailTab = normalizeDetailTab(searchParams.get('detailTab'));

  useEffect(() => {
    let cancelled = false;

    const loadScreen = async () => {
      if (!symbol) {
        return;
      }
      setLoading(true);
      setError('');
      setScreen(null);
      setImageUrl('');
      setImageFailed(false);
      setImageLoading(true);
      try {
        const payload = await fetchScannerDetail(symbol, {
          exchange,
          timeframe,
          patternEngineVersion,
          tradeEngineVersion,
        });
        if (!cancelled) {
          setScreen(payload);
        }
      } catch (requestError) {
        if (!cancelled) {
          setScreen(null);
          setError('L’analyse détaillée est indisponible pour cet actif.');
          setImageLoading(false);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void loadScreen();
    return () => {
      cancelled = true;
    };
  }, [exchange, patternEngineVersion, symbol, timeframe, tradeEngineVersion]);

  useEffect(() => {
    if (!authResolved || !user?.login || !symbol) {
      return undefined;
    }

    const abortController = new AbortController();
    void recordPatternScannerPageView({
      asset: symbol,
      timeframe,
      signal: abortController.signal,
    });

    return () => {
      abortController.abort();
    };
  }, [authResolved, symbol, timeframe, user?.login]);

  useEffect(() => {
    let cancelled = false;

    const loadAssetScannerSnapshot = async () => {
      if (!symbol) {
        setAssetScannerSnapshot(null);
        setAssetScannerHistory([]);
        setAssetScannerError('');
        setAssetScannerLoading(false);
        return;
      }

      setAssetScannerLoading(true);
      setAssetScannerError('');
      setAssetScannerSnapshot(null);
      setAssetScannerHistory([]);
      try {
        const [snapshot, history] = await Promise.all([
          fetchAssetMetricSnapshot(symbol),
          fetchAssetMetricHistory(symbol).catch(() => []),
        ]);
        if (!cancelled) {
          setAssetScannerSnapshot(snapshot);
          setAssetScannerHistory(Array.isArray(history) ? history : []);
        }
      } catch (requestError) {
        if (!cancelled) {
          setAssetScannerSnapshot(null);
          setAssetScannerHistory([]);
          setAssetScannerError('Le snapshot Asset Scanner est indisponible pour cet actif.');
        }
      } finally {
        if (!cancelled) {
          setAssetScannerLoading(false);
        }
      }
    };

    void loadAssetScannerSnapshot();
    return () => {
      cancelled = true;
    };
  }, [symbol]);

  useEffect(() => {
    let cancelled = false;
    let objectUrl = '';
    let retryTimer = null;

    const analysis = screen?.pattern;
    const resolvedPatternEngineVersion = screen?.engineVersions?.patternEngineVersion || patternEngineVersion;
    if (!analysis?.symbol) {
      setImageUrl('');
      setImageFailed(false);
      setImageLoading(false);
      return () => {};
    }

    const loadImage = async (attempt = 0, forceSymbolLookup = false) => {
      setImageLoading(true);
      const controller = new AbortController();
      const abortTimer = window.setTimeout(() => controller.abort(), 5000);

      try {
        const blob = await fetchPatternImageAsBlob(analysis, {
          forceSymbolLookup,
          signal: controller.signal,
          patternEngineVersion: resolvedPatternEngineVersion,
        });
        if (blob && !cancelled) {
          objectUrl = URL.createObjectURL(blob);
          setImageUrl(objectUrl);
          setImageFailed(false);
          setImageLoading(false);
          return;
        }
      } catch (requestError) {
      } finally {
        window.clearTimeout(abortTimer);
      }

      if (!cancelled && attempt < 15) {
        retryTimer = window.setTimeout(() => {
          void loadImage(attempt + 1, true);
        }, 2000);
      } else if (!cancelled) {
        setImageUrl('');
        setImageLoading(false);
      }
    };

    setImageFailed(false);
    setImageUrl('');
    void loadImage(0, !analysis.imagePath);

    return () => {
      cancelled = true;
      if (retryTimer) {
        window.clearTimeout(retryTimer);
      }
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [patternEngineVersion, screen]);

  const currentPattern = screen?.pattern;
  const currentSummary = screen?.summary;
  const tradePlan = screen?.tradePlan;
  const patternMetadata = useMemo(() => resolvePatternEngineV2Metadata(currentPattern), [currentPattern]);
  const geometrySnapshot = patternMetadata.geometry;
  const structureSnapshot = patternMetadata.structure;
  const structureEvents = patternMetadata.events;
  const patternFamilySnapshot = patternMetadata.patternFamily;
  const confidenceBreakdown = patternMetadata.confidenceBreakdown;
  const patternExplanation = patternMetadata.explanation;
  const swingSummary = useMemo(() => buildSwingSummary(patternMetadata.swings), [patternMetadata.swings]);
  const displayedPatternKey = resolveDisplayedPatternKey(currentPattern || currentSummary || {});
  const displayedPatternTitle = getPatternTitle(displayedPatternKey || currentSummary?.pattern || currentPattern?.patternDetected || '');
  const primaryPatternCandidate = useMemo(
    () => patternMetadata.patterns.find((entry) => entry.primary || entry.name === displayedPatternKey) || null,
    [displayedPatternKey, patternMetadata.patterns],
  );
  const alternativePatternCandidates = useMemo(
    () => patternMetadata.patterns.filter((entry) => entry.name !== displayedPatternKey).slice(0, 2),
    [displayedPatternKey, patternMetadata.patterns],
  );
  const geometryFlags = patternMetadata.fakeGeometryFlags || [];
  const isNoClearPattern = displayedPatternKey === NO_CLEAR_PATTERN_KEY;
  const isAuthenticated = Boolean(user?.login);
  const canViewTradePlan = authResolved && isAuthenticated;
  const convictionScore = resolveConvictionScore(screen || currentPattern || {});
  const convictionLabel = currentSummary?.convictionLabel || resolveConvictionLabel(convictionScore);
  const risk = resolveRiskDescriptor(currentPattern || {});
  const direction = resolveDirectionDescriptor(currentSummary?.bias || currentPattern?.direction);
  const tradeDirection = resolveTradeDirectionDescriptor(tradePlan?.direction);
  const alignment = resolveAlignmentDescriptor(screen || {});
  const multiTimeframeInsights = screen?.multiTimeframe?.insights;
  const multiTimeframeWarnings = screen?.multiTimeframe?.warnings || [];
  const effectivePatternEngineVersion = screen?.engineVersions?.patternEngineVersion || patternEngineVersion;
  const effectiveTradeEngineVersion = screen?.engineVersions?.tradeEngineVersion || tradeEngineVersion;
  const assetScannerLabel = assetScannerSnapshot?.code || assetScannerSnapshot?.symbol || currentSummary?.symbol || symbol;
  const assetScannerScore = resolveAssetScoreDescriptor(assetScannerSnapshot?.compositeScore);
  const assetScannerNarrative = buildAssetScannerNarrative(assetScannerSnapshot);
  const assetScannerHistoryWindow = useMemo(() => buildAssetHistoryWindow(assetScannerHistory), [assetScannerHistory]);
  const assetScannerPriceSeries = useMemo(() => extractNumericSeries(assetScannerHistoryWindow, 'price'), [assetScannerHistoryWindow]);
  const assetScannerScoreSeries = useMemo(() => extractNumericSeries(assetScannerHistoryWindow, 'compositeScore'), [assetScannerHistoryWindow]);
  const validationMatrix = useMemo(() => buildValidationMatrix(screen), [screen]);
  const detailSections = useMemo(() => buildDetailSections(screen), [screen]);
  const executionPlan = useMemo(
    () => buildExecutionPlan(currentPattern, {
      isV2: effectivePatternEngineVersion >= 2,
      displayedPatternTitle,
    }),
    [currentPattern, displayedPatternTitle, effectivePatternEngineVersion],
  );
  const tradePlanHighlights = useMemo(() => buildTradePlanHighlights(tradePlan), [tradePlan]);
  const tradePlanReasoning = useMemo(() => buildTradePlanReasoning(tradePlan), [tradePlan]);
  const returnTo = useMemo(() => {
    if (location.state?.returnTo) {
      return location.state.returnTo;
    }
    const params = new URLSearchParams({ exchange, timeframe });
    if (effectivePatternEngineVersion > DEFAULT_PATTERN_ENGINE_VERSION) {
      params.set('patternEngineVersion', String(effectivePatternEngineVersion));
    }
    if (effectiveTradeEngineVersion > DEFAULT_TRADE_ENGINE_VERSION) {
      params.set('tradeEngineVersion', String(effectiveTradeEngineVersion));
    }
    return `/scanner?${params.toString()}`;
  }, [effectivePatternEngineVersion, effectiveTradeEngineVersion, exchange, location.state, timeframe]);
  const isInitialScreenLoading = loading && !screen;
  const isRefreshingScreen = loading && !!screen;
  const isGraphPending = isInitialScreenLoading || (Boolean(currentPattern?.symbol) && !imageUrl && !imageFailed);
  const structureHierarchyPanel = effectivePatternEngineVersion >= 2 && (geometrySnapshot || structureSnapshot || patternMetadata.patterns.length > 0) ? (
    <Paper elevation={0} sx={{ p: 2.2, borderRadius: 4, border: '1px solid rgba(16,37,43,0.08)' }}>
      <Stack spacing={1.6}>
        <Box>
          <Typography variant="h6" sx={{ color: '#17323c', fontWeight: 900 }}>Lecture structurelle v2</Typography>
          <Typography variant="body2" sx={{ color: '#5c6d74', lineHeight: 1.7 }}>
            Le moteur v2 identifie d’abord la structure de marché, mesure les événements clés, classe les patterns compatibles, puis ajuste la confiance trade selon l’ambiguïté restante.
          </Typography>
        </Box>

        {(structureSnapshot || geometrySnapshot || patternFamilySnapshot) ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.1fr) minmax(0, 0.9fr)' }, gap: 1.2 }}>
            <Paper elevation={0} sx={{ p: 1.5, borderRadius: 3, background: '#fffaf2', border: '1px solid rgba(16,37,43,0.08)' }}>
              <Stack spacing={0.7}>
                <Typography variant="overline" sx={{ color: '#68777d', letterSpacing: '0.08em' }}>Structure dominante</Typography>
                <Typography variant="h6" sx={{ color: '#17323c', fontWeight: 900 }}>
                  {normalizeText(structureSnapshot?.label, geometrySnapshot ? `Structure ${resolveGeometryTypeLabel(geometrySnapshot.type)}` : 'n/a')}
                </Typography>
                <Typography variant="body2" sx={{ color: '#5c6d74', lineHeight: 1.7 }}>
                  {`Biais ${formatStructureBiasLabel(structureSnapshot?.bias)}${patternFamilySnapshot ? ` • Famille ${formatPatternFamilyLabel(patternFamilySnapshot.label || patternFamilySnapshot.code)}` : ''}`}
                </Typography>
                <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
                  {geometrySnapshot ? <Chip label={`Géométrie ${resolveGeometryTypeLabel(geometrySnapshot.type)}`} sx={{ bgcolor: '#eef4f6', color: '#17323c', fontWeight: 800 }} /> : null}
                  {structureSnapshot ? <Chip label={`Confiance structure ${formatConfidence(structureSnapshot.confidence)}`} sx={{ bgcolor: '#f5efe2', color: '#17323c', fontWeight: 800 }} /> : null}
                  {structureSnapshot ? <Chip label={`Qualité ${formatConfidence(structureSnapshot.quality)}`} sx={{ bgcolor: '#f5efe2', color: '#17323c', fontWeight: 800 }} /> : null}
                </Stack>
              </Stack>
            </Paper>

            {confidenceBreakdown ? (
              <Paper elevation={0} sx={{ p: 1.5, borderRadius: 3, background: '#fffdf8', border: '1px solid rgba(16,37,43,0.08)' }}>
                <Stack spacing={0.7}>
                  <Typography variant="overline" sx={{ color: '#68777d', letterSpacing: '0.08em' }}>Breakdown de confiance</Typography>
                  {buildConfidenceItems(confidenceBreakdown).map(([label, value]) => (
                    <Stack key={label} direction="row" justifyContent="space-between" spacing={1}>
                      <Typography variant="body2" sx={{ color: '#5c6d74' }}>{label}</Typography>
                      <Typography variant="body2" sx={{ color: '#17323c', fontWeight: 800 }}>{value}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            ) : null}
          </Box>
        ) : null}

        {isNoClearPattern ? (
          <Alert severity="warning" sx={{ borderRadius: 3 }}>
            Aucune figure claire : la qualité structurelle reste sous le seuil exploitable pour retenir un pattern principal fiable.
          </Alert>
        ) : null}

        {geometryFlags.length > 0 ? (
          <Alert severity="info" sx={{ borderRadius: 3 }}>
            {`Points de vigilance : ${geometryFlags.map(formatGeometryFlag).join(' · ')}`}
          </Alert>
        ) : null}

        {structureEvents ? (
          <Paper elevation={0} sx={{ p: 1.5, borderRadius: 3, background: '#fffdf8', border: '1px solid rgba(16,37,43,0.08)' }}>
            <Stack spacing={0.8}>
              <Typography variant="subtitle1" sx={{ color: '#17323c', fontWeight: 900 }}>Événements visibles</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 0.8 }}>
                {buildStructureEventItems(structureEvents).map(([label, value]) => (
                  <Paper key={label} elevation={0} sx={{ p: 1.1, borderRadius: 2.8, background: '#fff', border: '1px solid rgba(16,37,43,0.06)' }}>
                    <Typography variant="caption" sx={{ color: '#6b7a80', letterSpacing: '0.04em' }}>{label}</Typography>
                    <Typography variant="subtitle2" sx={{ color: '#17323c', fontWeight: 900, mt: 0.25 }}>{value}</Typography>
                  </Paper>
                ))}
              </Box>
            </Stack>
          </Paper>
        ) : null}

        {patternMetadata.patterns.length > 0 ? (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 1.1 }}>
            {patternMetadata.patterns.map((candidate) => (
              <Paper key={`${candidate.name}-${candidate.score}`} elevation={0} sx={{ p: 1.5, borderRadius: 3, background: candidate.name === displayedPatternKey ? 'rgba(23,50,60,0.06)' : '#fffaf2', border: '1px solid rgba(16,37,43,0.08)' }}>
                <Stack spacing={0.7}>
                  <Typography variant="overline" sx={{ color: '#68777d', letterSpacing: '0.08em' }}>
                    {candidate.name === displayedPatternKey && !isNoClearPattern ? 'Pattern principal' : 'Hypothèse'}
                  </Typography>
                  <Typography variant="subtitle1" sx={{ color: '#17323c', fontWeight: 800 }}>
                    {getPatternTitle(candidate.name)}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#5c6d74' }}>{`Score ${candidate.score}%`}</Typography>
                  {candidate.familyLabel ? <Typography variant="caption" sx={{ color: '#5d6e75' }}>{`Famille ${formatPatternFamilyLabel(candidate.familyLabel)}`}</Typography> : null}
                  {Number.isFinite(Number(candidate.gapToLeader)) && candidate.gapToLeader > 0 ? <Typography variant="caption" sx={{ color: '#5d6e75' }}>{`Écart ${candidate.gapToLeader} pts`}</Typography> : null}
                  {candidate.compatible === false ? <Typography variant="caption" sx={{ color: '#8d6c16' }}>Hors famille géométrique dominante</Typography> : null}
                </Stack>
              </Paper>
            ))}
          </Box>
        ) : null}

        {alternativePatternCandidates.length > 0 ? (
          <Typography variant="body2" sx={{ color: '#5c6d74' }}>
            {`Autres lectures possibles : ${alternativePatternCandidates.map((candidate) => `${getPatternTitle(candidate.name)} ${candidate.score}%`).join(' · ')}`}
          </Typography>
        ) : null}

        {swingSummary.length > 0 ? (
          <Paper elevation={0} sx={{ p: 1.5, borderRadius: 3, background: '#fffaf2', border: '1px solid rgba(16,37,43,0.08)' }}>
            <Stack spacing={0.7}>
              <Typography variant="subtitle1" sx={{ color: '#17323c', fontWeight: 900 }}>Swings significatifs</Typography>
              {swingSummary.map((entry) => (
                <Typography key={entry} variant="body2" sx={{ color: '#5c6d74', lineHeight: 1.7 }}>{entry}</Typography>
              ))}
            </Stack>
          </Paper>
        ) : null}

        {patternExplanation ? (
          <Paper elevation={0} sx={{ p: 1.5, borderRadius: 3, background: '#fffdf8', border: '1px solid rgba(16,37,43,0.08)' }}>
            <Stack spacing={0.8}>
              <Typography variant="subtitle1" sx={{ color: '#17323c', fontWeight: 900 }}>Pourquoi ce pattern ?</Typography>
              <Typography variant="body2" sx={{ color: '#5c6d74', lineHeight: 1.7 }}>
                {normalizeText(patternExplanation.summary, 'Le moteur structure d’abord le marché avant de classer les hypothèses de pattern.')}
              </Typography>
              {(patternExplanation.checks || []).map((check) => (
                <Typography key={`${check.label}-${check.passed}`} variant="body2" sx={{ color: check.passed ? '#1f7a4d' : '#8d6c16', lineHeight: 1.7 }}>
                  {`${check.passed ? '✓' : '✗'} ${check.label}`}
                </Typography>
              ))}
            </Stack>
          </Paper>
        ) : null}
      </Stack>
    </Paper>
  ) : null;

  const updateTimeframe = (_, nextTimeframe) => {
    if (!nextTimeframe) {
      return;
    }
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.set('timeframe', nextTimeframe);
    setSearchParams(nextSearchParams, { replace: true });
  };

  const updateDetailTab = (_, nextTab) => {
    if (!nextTab) {
      return;
    }
    const nextSearchParams = new URLSearchParams(searchParams);
    if (nextTab === DEFAULT_DETAIL_TAB) {
      nextSearchParams.delete('detailTab');
    } else {
      nextSearchParams.set('detailTab', nextTab);
    }
    setSearchParams(nextSearchParams, { replace: true });
  };

  return (
    <Box sx={{ minHeight: '100vh', background: 'radial-gradient(circle at top left, rgba(243,198,92,0.16), transparent 28%), linear-gradient(180deg, #fbf7ee 0%, #f2ece0 100%)', py: { xs: 4, md: 6 } }}>
      <Container maxWidth="xl">
        <Stack spacing={3}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.6} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
            <Button startIcon={<ArrowBackRoundedIcon />} onClick={() => navigate(returnTo)} sx={{ textTransform: 'none', fontWeight: 800 }}>
              Retour au scanner
            </Button>
            <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
              <Chip label={`${exchange} · ${timeframe}`} sx={{ bgcolor: '#eef4f6', color: '#17323c', fontWeight: 800 }} />
                <Chip label={`Pattern v${effectivePatternEngineVersion}`} sx={{ bgcolor: '#f5efe2', color: '#17323c', fontWeight: 800 }} />
                <Chip label={`Trade v${effectiveTradeEngineVersion}`} sx={{ bgcolor: '#f5efe2', color: '#17323c', fontWeight: 800 }} />
              <Chip label={isInitialScreenLoading ? 'Analyse en cours…' : `Analyse ${formatDateTime(currentPattern?.analysisTimestamp)}`} sx={{ bgcolor: '#f5efe2', color: '#17323c', fontWeight: 700 }} />
            </Stack>
          </Stack>

          {loading ? <LinearProgress sx={{ height: 8, borderRadius: 999 }} /> : null}
          {error ? <Alert severity="warning">{error}</Alert> : null}

          <Paper elevation={0} sx={{ p: { xs: 2.4, md: 3 }, borderRadius: 5, background: 'linear-gradient(135deg, #17323c 0%, #264653 100%)', color: '#fff8eb' }}>
            <Stack spacing={1.2}>
              <Typography variant="h3" sx={{ fontWeight: 900 }}>{isInitialScreenLoading ? 'Chargement de l’analyse…' : normalizeText(currentSummary?.symbol || symbol, 'Actif')}</Typography>
              {isInitialScreenLoading ? (
                <Typography variant="body1" sx={{ color: 'rgba(255,248,235,0.88)', lineHeight: 1.7, maxWidth: 720 }}>
                  Les signaux, niveaux et indicateurs détaillés de cette fenêtre sont en cours de chargement.
                </Typography>
              ) : (
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip label={displayedPatternTitle} sx={{ bgcolor: 'rgba(255,248,235,0.12)', color: '#fff8eb', fontWeight: 800 }} />
                  <Chip label={direction.label} sx={{ bgcolor: direction.background, color: direction.tone, fontWeight: 800 }} />
                  <Chip label={convictionLabel} sx={{ bgcolor: '#f3c65c', color: '#17323c', fontWeight: 900 }} />
                  <Chip label={`Conviction ${convictionScore}`} sx={{ bgcolor: 'rgba(255,248,235,0.12)', color: '#fff8eb', fontWeight: 800 }} />
                  {geometrySnapshot ? <Chip label={`Géométrie ${resolveGeometryTypeLabel(geometrySnapshot.type)}`} sx={{ bgcolor: 'rgba(255,248,235,0.12)', color: '#fff8eb', fontWeight: 800 }} /> : null}
                </Stack>
              )}
            </Stack>
          </Paper>

          <Paper elevation={0} sx={{ borderRadius: 4.5, border: '1px solid rgba(16,37,43,0.08)', background: 'rgba(255,253,248,0.88)', overflow: 'hidden' }}>
            <Tabs
              value={activeDetailTab}
              onChange={updateDetailTab}
              variant="scrollable"
              allowScrollButtonsMobile
              aria-label="Navigation du détail Pattern Scanner"
              sx={{
                px: { xs: 0.8, md: 1.2 },
                py: 0.5,
                '& .MuiTab-root': {
                  minHeight: 50,
                  fontWeight: 900,
                  textTransform: 'none',
                  alignItems: 'flex-start',
                  color: '#5d6e75',
                },
                '& .Mui-selected': {
                  color: '#17323c',
                },
                '& .MuiTabs-indicator': {
                  height: 3,
                  borderRadius: 999,
                  backgroundColor: '#17323c',
                },
              }}
            >
              {DETAIL_PAGE_TABS.map((tab) => (
                <Tab
                  key={tab.value}
                  id={`scanner-detail-tab-${tab.value}`}
                  aria-controls={`scanner-detail-tabpanel-${tab.value}`}
                  value={tab.value}
                  label={tab.label}
                />
              ))}
            </Tabs>
          </Paper>

          <DetailTabPanel value="pattern" activeValue={activeDetailTab}>
            <Stack spacing={3}>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))', lg: effectivePatternEngineVersion >= 2 ? 'repeat(3, minmax(0, 1fr))' : geometrySnapshot ? 'repeat(3, minmax(0, 1fr))' : 'repeat(2, minmax(0, 1fr))' }, gap: 1.2 }}>
                <SummaryTile label={effectivePatternEngineVersion >= 2 ? 'Pattern principal' : 'Pattern'} value={displayedPatternTitle} helper={effectivePatternEngineVersion >= 2 ? (isNoClearPattern ? 'La structure reste trop ambiguë pour retenir une figure exploitable.' : 'Figure principale retenue par le moteur géométrique.') : 'Structure dominante détectée sur la fenêtre active.'} tone="#17323c" loading={isInitialScreenLoading} />
                <SummaryTile label="Biais" value={direction.label} helper="Direction tactique la plus cohérente à ce stade." tone={direction.tone} loading={isInitialScreenLoading} />
                <SummaryTile label="Conviction" value={`${convictionScore} / 100`} helper={convictionLabel} tone="#214f77" loading={isInitialScreenLoading} />
                <SummaryTile label="Risque" value={risk.label} helper="Lecture combinée du fakeout, du R/R et de la cible." tone={risk.tone} loading={isInitialScreenLoading} />
                {geometrySnapshot ? <SummaryTile label="Geometry Confidence" value={formatConfidence(geometrySnapshot.confidence)} helper={`Structure ${resolveGeometryTypeLabel(geometrySnapshot.type)}`} tone="#8d6c16" loading={isInitialScreenLoading} /> : null}
                {geometrySnapshot ? <SummaryTile label="Structure Quality" value={formatConfidence(geometrySnapshot.quality)} helper="Propreté de la structure avant volume et momentum." tone="#5f6f75" loading={isInitialScreenLoading} /> : null}
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 3fr) minmax(300px, 1fr)' }, gap: 1.6 }}>
                <Stack spacing={1.6}>
                  <Paper component="section" aria-label="Graph principal" elevation={0} sx={{ p: 2.2, borderRadius: 4, border: '1px solid rgba(16,37,43,0.08)' }}>
                    <Stack spacing={1.4}>
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
                        <Typography variant="h5" sx={{ color: '#17323c', fontWeight: 900 }}>Graph principal</Typography>
                        {isInitialScreenLoading ? (
                          <Chip label="Niveaux en cours de chargement" sx={{ bgcolor: '#f5efe2', color: '#17323c', fontWeight: 800 }} />
                        ) : (
                          <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
                            <Chip label={`Cassure ${formatPrice(currentPattern?.breakoutLevel)}`} sx={{ bgcolor: '#eef4f6', color: '#17323c', fontWeight: 800 }} />
                            <Chip label={`Cible ${formatPrice(currentPattern?.targetPrice)}`} sx={{ bgcolor: 'rgba(31,122,77,0.12)', color: '#1f7a4d', fontWeight: 800 }} />
                            <Chip label={`Invalidation ${formatPrice(currentPattern?.stopInvalidation)}`} sx={{ bgcolor: 'rgba(170,61,51,0.12)', color: '#aa3d33', fontWeight: 800 }} />
                          </Stack>
                        )}
                      </Stack>

                      {isRefreshingScreen ? (
                        <SectionLoadingNotice message={`Actualisation du graph ${timeframe} en cours. Les niveaux et le visuel se mettent à jour.`} />
                      ) : null}

                      <Tabs
                        value={timeframe}
                        onChange={updateTimeframe}
                        variant="scrollable"
                        allowScrollButtonsMobile
                        aria-label="Changer de timeframe du graph principal"
                        sx={{
                          minHeight: 44,
                          '& .MuiTab-root': {
                            minHeight: 44,
                            fontWeight: 800,
                            textTransform: 'none',
                          },
                        }}
                      >
                        {SCANNER_DETAIL_TIMEFRAMES.map((entry) => (
                          <Tab key={entry} value={entry} label={entry} />
                        ))}
                      </Tabs>

                      <Box sx={{ minHeight: 460, borderRadius: 3.5, overflow: 'hidden', background: 'linear-gradient(180deg, rgba(245,239,226,0.9) 0%, rgba(255,255,255,0.98) 100%)', border: '1px solid rgba(16,37,43,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {isGraphPending ? (
                          <Stack spacing={1.2} alignItems="center">
                            <LinearProgress sx={{ width: 220, height: 8, borderRadius: 999 }} />
                            <Typography variant="body2" sx={{ color: '#5c6d74', fontWeight: 700 }}>Données du bloc en cours de chargement…</Typography>
                            <Typography variant="body2" sx={{ color: '#5c6d74', textAlign: 'center', maxWidth: 460 }}>
                              {isInitialScreenLoading
                                ? 'Le graphique principal, les annotations et les niveaux sont en préparation.'
                                : 'Le visuel annoté est en cours de génération ou de chargement pour cette fenêtre.'}
                            </Typography>
                          </Stack>
                        ) : imageUrl ? (
                          <Box component="img" src={imageUrl} alt={`Graph pattern ${symbol}`} sx={{ width: '100%', height: '100%', objectFit: 'contain', background: '#fff' }} />
                        ) : (
                          <Stack spacing={1.2} alignItems="center" sx={{ px: 3 }}>
                            <ShowChartRoundedIcon sx={{ fontSize: 54, color: '#8d6c16' }} />
                            <Typography variant="h6" sx={{ color: '#17323c', fontWeight: 800 }}>Visuel annoté indisponible</Typography>
                            <Typography variant="body2" sx={{ color: '#5c6d74', textAlign: 'center', maxWidth: 460 }}>
                              {imageFailed
                                ? 'Le moteur n’a pas encore exposé une image lisible pour cette fenêtre. Les niveaux et la lecture détaillée restent disponibles ci-dessous.'
                                : 'Le graphique annoté sera affiché dès que la génération aura terminé.'}
                            </Typography>
                          </Stack>
                        )}
                      </Box>

                    </Stack>
                  </Paper>

                  {structureHierarchyPanel}
                </Stack>

                <Paper elevation={0} sx={{ p: 2.2, borderRadius: 4, border: '1px solid rgba(16,37,43,0.08)' }}>
                  <Stack spacing={1.5}>
                    <Typography variant="h6" sx={{ color: '#17323c', fontWeight: 900 }}>Panneau contexte</Typography>
                    {loading ? (
                      <SectionLoadingNotice
                        message={isInitialScreenLoading
                          ? 'Contexte, validations et plan d’exécution en cours de chargement.'
                          : `Actualisation du contexte ${timeframe} en cours.`}
                      />
                    ) : null}

                    {!screen ? null : (
                      <>
                        <Chip label={`Conviction ${convictionScore} · ${convictionLabel}`} sx={{ alignSelf: 'flex-start', bgcolor: '#17323c', color: '#fff8eb', fontWeight: 900 }} />

<Box>
                          <Typography variant="subtitle2" sx={{ color: '#17323c', fontWeight: 800, mb: 0.8 }}>Plan d'exécution</Typography>
                          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', xl: effectivePatternEngineVersion >= 2 ? 'repeat(3, minmax(0, 1fr))' : 'repeat(2, minmax(0, 1fr))' }, gap: 0.9 }}>
                            {executionPlan.map((item) => (
                              <Paper key={item.label} elevation={0} sx={{ p: 1.1, borderRadius: 2.8, background: '#fffdf8', border: '1px solid rgba(16,37,43,0.06)' }}>
                                <Typography variant="caption" sx={{ color: '#6b7a80', letterSpacing: '0.04em' }}>{item.label}</Typography>
                                <Typography variant="subtitle2" sx={{ color: '#17323c', fontWeight: 900, mt: 0.25 }}>{item.value}</Typography>
                                <Typography variant="caption" sx={{ color: '#5d6e75', display: 'block', mt: 0.45, lineHeight: 1.6 }}>{item.helper}</Typography>
                              </Paper>
                            ))}
                          </Box>
                        </Box>

                         <Divider />


                        <Box sx={{ display: 'grid', gap: 0.9 }}>
                          {validationMatrix.map((item) => (
                            <Paper key={item.label} elevation={0} sx={{ p: 1.2, borderRadius: 2.8, background: '#fffdf8', border: '1px solid rgba(16,37,43,0.06)' }}>
                              <Stack direction="row" justifyContent="space-between" spacing={1.5}>
                                <Typography variant="subtitle2" sx={{ color: '#17323c', fontWeight: 800 }}>{item.label}</Typography>
                                <Typography variant="subtitle2" sx={{ color: item.passed ? '#1f7a4d' : '#aa3d33', fontWeight: 900 }}>{item.passed ? '✓' : '✗'}</Typography>
                              </Stack>
                              <Typography variant="caption" sx={{ color: '#5d6e75', display: 'block', mt: 0.45, lineHeight: 1.6 }}>{item.helper}</Typography>
                            </Paper>
                          ))}
                        </Box>

                       
                        

                        <Box>
                          <Typography variant="subtitle2" sx={{ color: '#17323c', fontWeight: 800, mb: 0.8 }}>Dominante multi-timeframe</Typography>
                          <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap sx={{ mb: 0.9 }}>
                            <Chip label={`Alignement ${Number.isFinite(multiTimeframeInsights?.alignmentScore) ? multiTimeframeInsights.alignmentScore : 'n/a'}`} sx={{ bgcolor: '#eef4f6', color: '#17323c', fontWeight: 800 }} />
                            <Chip label={`Conviction MTF ${Number.isFinite(multiTimeframeInsights?.convictionScore) ? multiTimeframeInsights.convictionScore : 'n/a'}`} sx={{ bgcolor: '#f5efe2', color: '#17323c', fontWeight: 800 }} />
                            <Chip label={`Volume ${Number.isFinite(multiTimeframeInsights?.volumeAlignmentScore) ? multiTimeframeInsights.volumeAlignmentScore : 'n/a'}`} sx={{ bgcolor: 'rgba(31,122,77,0.12)', color: '#1f7a4d', fontWeight: 800 }} />
                          </Stack>
                          <Typography variant="body2" sx={{ color: '#5d6e75', lineHeight: 1.7 }}>
                            {normalizeText(
                              multiTimeframeInsights?.multiTfNarrative,
                              `Direction dominante ${normalizeText(multiTimeframeInsights?.dominantDirection, 'n/a')} sur ${normalizeText(multiTimeframeInsights?.dominantTimeframe, 'n/a')}.`,
                            )}
                          </Typography>
                          {multiTimeframeWarnings.length > 0 ? (
                            <Alert severity="warning" sx={{ mt: 1.1 }}>
                              {multiTimeframeWarnings.join(' · ')}
                            </Alert>
                          ) : null}
                        </Box>

                        <Box>
                          <Typography variant="subtitle2" sx={{ color: '#17323c', fontWeight: 800, mb: 0.45 }}>Risque principal</Typography>
                          <Typography variant="body2" sx={{ color: '#5d6e75', lineHeight: 1.7 }}>
                            {normalizeText(screen?.graph?.volumeComment, clampSummaryText(currentPattern?.aiSummary))}
                          </Typography>
                        </Box>

                        <Box>
                          <Typography variant="subtitle2" sx={{ color: '#17323c', fontWeight: 800, mb: 0.45 }}>Résumé IA</Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              color: '#5d6e75',
                              lineHeight: 1.7,
                              display: '-webkit-box',
                              WebkitBoxOrient: 'vertical',
                              WebkitLineClamp: 3,
                              overflow: 'hidden',
                            }}
                          >
                            {clampSummaryText(currentSummary?.aiSummary || currentPattern?.aiSummary)}
                          </Typography>
                        </Box>

                        <Chip label={`Alignement MTF ${alignment.label}`} sx={{ alignSelf: 'flex-start', bgcolor: alignment.background, color: alignment.tone, fontWeight: 800 }} />
                      </>
                    )}
                  </Stack>
                </Paper>
              </Box>
            </Stack>
          </DetailTabPanel>

          <DetailTabPanel value="trade-plan" activeValue={activeDetailTab}>
            <Paper component="section" aria-label="Trade Plan" elevation={0} sx={{ p: 2.2, borderRadius: 4, border: '1px solid rgba(16,37,43,0.08)', background: 'linear-gradient(180deg, rgba(255,253,248,0.96) 0%, rgba(248,244,236,1) 100%)' }}>
              <Stack spacing={1.5}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.1} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
                  <Box>
                    <Typography variant="h5" sx={{ color: '#17323c', fontWeight: 900 }}>Trade Plan</Typography>
                    <Typography variant="body2" sx={{ color: '#5c6d74', lineHeight: 1.7, mt: 0.4 }}>
                      Le moteur reste probabiliste et n’émet jamais un ordre d’achat ou de vente, seulement le scénario actuellement le plus robuste.
                    </Typography>
                  </Box>
                  {canViewTradePlan && tradePlan ? (
                    <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
                      <Chip label={tradeDirection.label} sx={{ bgcolor: tradeDirection.background, color: tradeDirection.tone, fontWeight: 900 }} />
                      {tradePlan.displayEligible ? (
                        <>
                          <Chip label={`Trade Grade ${normalizeText(tradePlan.tradeGrade, 'n/a')}`} sx={{ bgcolor: '#17323c', color: '#fff8eb', fontWeight: 800 }} />
                          <Chip label={`RR ${formatRiskReward(tradePlan.riskReward)}`} sx={{ bgcolor: '#f3c65c', color: '#17323c', fontWeight: 900 }} />
                        </>
                      ) : (
                        <Chip label="Aucun trade suggéré" sx={{ bgcolor: '#f5efe2', color: '#17323c', fontWeight: 800 }} />
                      )}
                    </Stack>
                  ) : !canViewTradePlan ? (
                    <Chip label={authResolved ? 'Connexion requise' : 'Vérification de session'} sx={{ bgcolor: '#f5efe2', color: '#17323c', fontWeight: 800 }} />
                  ) : null}
                </Stack>

                {canViewTradePlan && loading && !tradePlan ? (
                  <SectionLoadingNotice message="Trade plan en cours de génération." />
                ) : null}

                {!canViewTradePlan ? (
                  <Paper elevation={0} sx={{ p: 1.8, borderRadius: 3.2, background: '#fff', border: '1px solid rgba(16,37,43,0.06)' }}>
                    <Stack spacing={1}>
                      <Typography variant="subtitle1" sx={{ color: '#17323c', fontWeight: 900 }}>
                        Bloc réservé aux utilisateurs connectés
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#5d6e75', lineHeight: 1.75 }}>
                        Le Trade Plan transforme le pattern détecté en scénario exploitable avec zone d’entrée, invalidation, objectifs, ratio rendement/risque,
                        scénario alternatif et logique de décision du moteur.
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#5d6e75', lineHeight: 1.75 }}>
                        Connectez-vous pour afficher ce contenu détaillé sans exposer les niveaux ni les paramètres d’exécution en mode invité.
                      </Typography>
                      {!authResolved ? (
                        <Typography variant="caption" sx={{ color: '#6b7a80' }}>
                          Vérification de votre session en cours.
                        </Typography>
                      ) : null}
                    </Stack>
                  </Paper>
                ) : tradePlan ? (
                  <>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(4, minmax(0, 1fr))' }, gap: 0.9 }}>
                      {tradePlanHighlights.map((item) => (
                        <Paper key={item.label} elevation={0} sx={{ p: 1.2, borderRadius: 2.8, background: '#fff', border: '1px solid rgba(16,37,43,0.06)' }}>
                          <Typography variant="caption" sx={{ color: '#6b7a80', letterSpacing: '0.04em' }}>{item.label}</Typography>
                          <Typography variant="subtitle1" sx={{ color: '#17323c', fontWeight: 900, mt: 0.25 }}>{item.value}</Typography>
                          <Typography variant="caption" sx={{ color: '#5d6e75', display: 'block', mt: 0.45, lineHeight: 1.6 }}>{item.helper}</Typography>
                        </Paper>
                      ))}
                    </Box>

                    {tradePlan.displayEligible ? (
                      <>
                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.3fr) minmax(0, 1fr)' }, gap: 1.2 }}>
                          <Paper elevation={0} sx={{ p: 1.6, borderRadius: 3.2, background: '#fff', border: '1px solid rgba(16,37,43,0.06)' }}>
                            <Typography variant="subtitle2" sx={{ color: '#17323c', fontWeight: 900, mb: 0.9 }}>Zone d’entrée</Typography>
                            <Stack spacing={0.7}>
                              <Typography variant="h6" sx={{ color: '#17323c', fontWeight: 900 }}>
                                {formatPrice(tradePlan.entry?.min)} → {formatPrice(tradePlan.entry?.max)}
                              </Typography>
                              <Chip label={normalizeText(tradePlan.entry?.type, 'n/a').replaceAll('_', ' ')} sx={{ alignSelf: 'flex-start', bgcolor: '#eef4f6', color: '#17323c', fontWeight: 800 }} />
                              <Typography variant="body2" sx={{ color: '#5d6e75', lineHeight: 1.7 }}>{normalizeText(tradePlan.entry?.reason, 'n/a')}</Typography>
                            </Stack>
                          </Paper>

                          <Paper elevation={0} sx={{ p: 1.6, borderRadius: 3.2, background: '#fff', border: '1px solid rgba(16,37,43,0.06)' }}>
                            <Typography variant="subtitle2" sx={{ color: '#17323c', fontWeight: 900, mb: 0.9 }}>Stop d’invalidation</Typography>
                            <Stack spacing={0.7}>
                              <Typography variant="h6" sx={{ color: '#aa3d33', fontWeight: 900 }}>{formatPrice(tradePlan.stopLoss?.price)}</Typography>
                              <Typography variant="body2" sx={{ color: '#5d6e75', lineHeight: 1.7 }}>
                                Distance {Number.isFinite(tradePlan.stopLoss?.distancePercent) ? `${tradePlan.stopLoss.distancePercent.toFixed(1)} %` : 'n/a'}
                              </Typography>
                              <Typography variant="body2" sx={{ color: '#5d6e75', lineHeight: 1.7 }}>{normalizeText(tradePlan.stopLoss?.reason, 'n/a')}</Typography>
                            </Stack>
                          </Paper>
                        </Box>

                        <Box>
                          <Typography variant="subtitle2" sx={{ color: '#17323c', fontWeight: 900, mb: 0.8 }}>Objectifs</Typography>
                          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' }, gap: 0.9 }}>
                            {(tradePlan.takeProfits || []).map((target) => (
                              <Paper key={target.label} elevation={0} sx={{ p: 1.2, borderRadius: 2.8, background: '#fff', border: '1px solid rgba(16,37,43,0.06)' }}>
                                <Typography variant="caption" sx={{ color: '#6b7a80', letterSpacing: '0.04em' }}>{target.label}</Typography>
                                <Typography variant="subtitle1" sx={{ color: '#17323c', fontWeight: 900, mt: 0.25 }}>{formatPrice(target.price)}</Typography>
                                <Typography variant="caption" sx={{ color: '#5d6e75', display: 'block', mt: 0.3 }}>RR {formatRiskReward(target.rr)}</Typography>
                                <Typography variant="caption" sx={{ color: '#5d6e75', display: 'block', mt: 0.15 }}>Probabilité {formatProbability(target.probability)}</Typography>
                                <Typography variant="caption" sx={{ color: '#5d6e75', display: 'block', mt: 0.45, lineHeight: 1.6 }}>{normalizeText(target.reason, 'n/a')}</Typography>
                              </Paper>
                            ))}
                          </Box>
                        </Box>

                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(0, 1.1fr) minmax(0, 1fr)' }, gap: 1.2 }}>
                          <Paper elevation={0} sx={{ p: 1.6, borderRadius: 3.2, background: '#fff', border: '1px solid rgba(16,37,43,0.06)' }}>
                            <Typography variant="subtitle2" sx={{ color: '#17323c', fontWeight: 900, mb: 0.9 }}>Levier prudent conseillé</Typography>
                            <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap sx={{ mb: 0.9 }}>
                              {(tradePlan.recommendedLeverage?.allowedModes || []).map((mode) => (
                                <Chip key={mode} label={mode} sx={{ bgcolor: '#eef4f6', color: '#17323c', fontWeight: 800 }} />
                              ))}
                            </Stack>
                            <Typography variant="body2" sx={{ color: '#5d6e75', lineHeight: 1.7 }}>
                              {normalizeText(tradePlan.reasoning?.whyLeverage, 'Le moteur limite volontairement le levier pour éviter les plans agressifs.')}
                            </Typography>
                          </Paper>

                          <Paper elevation={0} sx={{ p: 1.6, borderRadius: 3.2, background: '#fff', border: '1px solid rgba(16,37,43,0.06)' }}>
                            <Typography variant="subtitle2" sx={{ color: '#17323c', fontWeight: 900, mb: 0.9 }}>Scénario alternatif</Typography>
                            <Stack spacing={0.7}>
                              <Chip label={`${resolveTradeDirectionDescriptor(tradePlan.alternativeScenario?.direction).label} · ${formatProbability(tradePlan.alternativeScenario?.probability)}`} sx={{ alignSelf: 'flex-start', bgcolor: '#f5efe2', color: '#17323c', fontWeight: 800 }} />
                              <Typography variant="body2" sx={{ color: '#5d6e75', lineHeight: 1.7 }}>{normalizeText(tradePlan.alternativeScenario?.condition, 'n/a')}</Typography>
                            </Stack>
                          </Paper>
                        </Box>

                        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }, gap: 1.2 }}>
                          <Paper elevation={0} sx={{ p: 1.6, borderRadius: 3.2, background: '#fff', border: '1px solid rgba(16,37,43,0.06)' }}>
                            <Typography variant="subtitle2" sx={{ color: '#17323c', fontWeight: 900, mb: 0.9 }}>Conditions d’annulation</Typography>
                            <Stack spacing={0.65}>
                              {(tradePlan.cancellationConditions || []).map((item) => (
                                <Typography key={item} variant="body2" sx={{ color: '#5d6e75', lineHeight: 1.7 }}>• {item}</Typography>
                              ))}
                            </Stack>
                          </Paper>

                          <Paper elevation={0} sx={{ p: 1.6, borderRadius: 3.2, background: '#fff', border: '1px solid rgba(16,37,43,0.06)' }}>
                            <Typography variant="subtitle2" sx={{ color: '#17323c', fontWeight: 900, mb: 0.9 }}>Trade Summary</Typography>
                            <Typography variant="body2" sx={{ color: '#5d6e75', lineHeight: 1.8 }}>{clampSummaryText(tradePlan.summary)}</Typography>
                          </Paper>
                        </Box>

                        {(tradePlan.profiles || []).length > 0 ? (
                          <Box>
                            <Typography variant="subtitle2" sx={{ color: '#17323c', fontWeight: 900, mb: 0.8 }}>Profils de trade</Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 0.9 }}>
                              {tradePlan.profiles.map((profile) => (
                                <Paper key={profile.name} elevation={0} sx={{ p: 1.2, borderRadius: 2.8, background: '#fff', border: '1px solid rgba(16,37,43,0.06)' }}>
                                  <Typography variant="subtitle2" sx={{ color: '#17323c', fontWeight: 900 }}>{profile.name}</Typography>
                                  <Typography variant="caption" sx={{ color: '#6b7a80', display: 'block', mt: 0.3 }}>{normalizeText(profile.entryType, 'n/a').replaceAll('_', ' ')}</Typography>
                                  <Typography variant="caption" sx={{ color: '#5d6e75', display: 'block', mt: 0.2 }}>RR {formatRiskReward(profile.riskReward)}</Typography>
                                  <Typography variant="caption" sx={{ color: '#5d6e75', display: 'block', mt: 0.2 }}>Levier {normalizeText(profile.leverage, 'n/a')}</Typography>
                                  <Typography variant="body2" sx={{ color: '#5d6e75', lineHeight: 1.7, mt: 0.7 }}>{normalizeText(profile.description, 'n/a')}</Typography>
                                </Paper>
                              ))}
                            </Box>
                          </Box>
                        ) : null}
                      </>
                    ) : (
                      <Alert severity="info" sx={{ borderRadius: 2.8 }}>
                        {normalizeText(tradePlan.displayMessage, 'Aucun plan de trading proposé actuellement.')}
                      </Alert>
                    )}

                    <Accordion disableGutters elevation={0} sx={{ borderRadius: 3.2, border: '1px solid rgba(16,37,43,0.08)', overflow: 'hidden' }}>
                      <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} sx={{ background: '#fff' }}>
                        <Typography variant="subtitle1" sx={{ color: '#17323c', fontWeight: 900 }}>Voir le raisonnement du moteur</Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ background: '#fcfbf7' }}>
                        <Stack spacing={1.1}>
                          {tradePlanReasoning.map(([label, value]) => (
                            <Box key={label}>
                              <Typography variant="subtitle2" sx={{ color: '#17323c', fontWeight: 800 }}>{label}</Typography>
                              <Typography variant="body2" sx={{ color: '#5d6e75', lineHeight: 1.75, mt: 0.25 }}>{value}</Typography>
                            </Box>
                          ))}
                        </Stack>
                      </AccordionDetails>
                    </Accordion>
                  </>
                ) : null}
              </Stack>
            </Paper>
          </DetailTabPanel>

          <DetailTabPanel value="asset-scanner" activeValue={activeDetailTab}>
            <Paper component="section" aria-label="Asset Scanner" elevation={0} sx={{ p: 2.2, borderRadius: 4, border: '1px solid rgba(16,37,43,0.08)', background: 'linear-gradient(180deg, rgba(255,253,248,0.96) 0%, rgba(252,249,242,1) 100%)' }}>
            <Stack spacing={1.5}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.1} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
                <Box>
                  <Typography variant="h5" sx={{ color: '#17323c', fontWeight: 900 }}>Asset Scanner</Typography>
                  <Typography variant="body2" sx={{ color: '#5c6d74', lineHeight: 1.7, mt: 0.4 }}>
                    Synthèse embarquée du snapshot scanner asset pour replacer cet actif dans la hiérarchie globale du radar.
                  </Typography>
                </Box>
                {assetScannerSnapshot ? (
                  <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
                    <Chip label={assetScannerLabel} sx={{ bgcolor: '#17323c', color: '#fff8eb', fontWeight: 900 }} />
                    <Chip label={assetScannerScore.label} sx={{ bgcolor: assetScannerScore.background, color: assetScannerScore.tone, fontWeight: 800 }} />
                    {assetScannerSnapshot?.sector ? (
                      <Chip label={assetScannerSnapshot.sector} sx={{ bgcolor: '#eef4f6', color: '#17323c', fontWeight: 800 }} />
                    ) : null}
                    {assetScannerSnapshot?.timestamp ? (
                      <Chip label={`Snapshot ${formatDateTime(assetScannerSnapshot.timestamp)}`} sx={{ bgcolor: '#f5efe2', color: '#17323c', fontWeight: 800 }} />
                    ) : null}
                  </Stack>
                ) : null}
              </Stack>

              {assetScannerLoading ? <SectionLoadingNotice message="Bloc Asset Scanner en cours de chargement." /> : null}
              {!assetScannerLoading && assetScannerError ? <Alert severity="info" sx={{ borderRadius: 2.8 }}>{assetScannerError}</Alert> : null}

              {assetScannerSnapshot ? (
                <>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' }, gap: 0.9 }}>
                    {[
                      ['Score composite', formatAssetMetric(assetScannerSnapshot.compositeScore), 'Priorisation globale du scanner asset.', assetScannerScore.tone],
                      ['Momentum', formatAssetMetric(assetScannerSnapshot.momentumScore), 'Vitesse et persistance du flux sur le snapshot.', '#214f77'],
                      ['Trend strength', formatAssetMetric(assetScannerSnapshot.trendStrength), 'Qualité de la tendance active suivie par le scanner.', '#17323c'],
                      ['Risque', formatAssetMetric(assetScannerSnapshot.riskScore), 'Lecture risque du snapshot asset.', '#8d6c16'],
                      ['Performance 24h', formatAssetSignedPercent(assetScannerSnapshot.perf24h), 'Variation journalière observée par le scanner.', '#1f7a4d'],
                      ['Performance 7j', formatAssetSignedPercent(assetScannerSnapshot.perf7d), 'Variation hebdomadaire retenue par le snapshot.', '#1f7a4d'],
                      ['Pattern score', formatAssetMetric(assetScannerSnapshot.patternScore), 'Force interne du pattern côté asset scanner.', '#17323c'],
                      ['Confiance pattern', formatPercent(assetScannerSnapshot.patternConfidence), 'Confiance relative du pattern conservé.', '#17323c'],
                    ].map(([label, value, helper, tone]) => (
                      <Paper key={label} elevation={0} sx={{ p: 1.2, borderRadius: 2.8, background: '#fff', border: '1px solid rgba(16,37,43,0.06)' }}>
                        <Typography variant="caption" sx={{ color: '#6b7a80', letterSpacing: '0.04em' }}>{label}</Typography>
                        <Typography variant="subtitle1" sx={{ color: tone, fontWeight: 900, mt: 0.25 }}>{value}</Typography>
                        <Typography variant="caption" sx={{ color: '#5d6e75', display: 'block', mt: 0.45, lineHeight: 1.6 }}>{helper}</Typography>
                      </Paper>
                    ))}
                  </Box>

                  <Paper elevation={0} sx={{ p: 1.6, borderRadius: 3.2, background: '#fff', border: '1px solid rgba(16,37,43,0.06)' }}>
                    <Stack spacing={1.1}>
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
                        <Typography variant="subtitle2" sx={{ color: '#17323c', fontWeight: 900 }}>Historique Asset Scanner</Typography>
                        <Chip label={`${assetScannerHistoryWindow.length} snapshots`} sx={{ bgcolor: '#eef4f6', color: '#17323c', fontWeight: 800 }} />
                      </Stack>

                      {assetScannerHistoryWindow.length > 0 ? (
                        <>
                          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }, gap: 1.1 }}>
                            <Paper elevation={0} sx={{ p: 1.2, borderRadius: 2.8, background: '#fffdf8', border: '1px solid rgba(16,37,43,0.06)' }}>
                              <Typography variant="caption" sx={{ color: '#6b7a80', letterSpacing: '0.04em' }}>Prix récent</Typography>
                              <Box sx={{ mt: 0.9 }}>
                                <Sparklines data={assetScannerPriceSeries} width={180} height={42} margin={6}>
                                  <SparklinesLine color="#214f77" style={{ strokeWidth: 2, fill: 'none' }} />
                                </Sparklines>
                              </Box>
                              <Typography variant="caption" sx={{ color: '#5d6e75', display: 'block', mt: 0.45 }}>Fenêtre des derniers snapshots Asset Scanner.</Typography>
                            </Paper>

                            <Paper elevation={0} sx={{ p: 1.2, borderRadius: 2.8, background: '#fffdf8', border: '1px solid rgba(16,37,43,0.06)' }}>
                              <Typography variant="caption" sx={{ color: '#6b7a80', letterSpacing: '0.04em' }}>Score composite récent</Typography>
                              <Box sx={{ mt: 0.9 }}>
                                <Sparklines data={assetScannerScoreSeries} width={180} height={42} margin={6}>
                                  <SparklinesLine color={assetScannerScore.tone} style={{ strokeWidth: 2, fill: 'none' }} />
                                </Sparklines>
                              </Box>
                              <Typography variant="caption" sx={{ color: '#5d6e75', display: 'block', mt: 0.45 }}>Évolution du rang tactique de l’actif dans le scanner.</Typography>
                            </Paper>
                          </Box>

                          <Stack spacing={0.7}>
                            {assetScannerHistoryWindow.slice(-4).reverse().map((entry) => (
                              <Stack key={`${entry.timestamp}-${entry.symbol || assetScannerLabel}`} direction="row" spacing={1.1} alignItems="center" justifyContent="space-between">
                                <Stack direction="row" spacing={0.9} alignItems="center">
                                  <TimelineRoundedIcon sx={{ fontSize: 18, color: '#8d6c16' }} />
                                  <Typography variant="body2" sx={{ color: '#17323c', fontWeight: 700 }}>{formatDateTime(entry.timestamp)}</Typography>
                                </Stack>
                                <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap justifyContent="flex-end">
                                  <Chip label={`Score ${formatAssetMetric(entry.compositeScore)}`} size="small" sx={{ bgcolor: '#eef4f6', color: '#17323c', fontWeight: 800 }} />
                                  <Chip label={`24h ${formatAssetSignedPercent(entry.change24h)}`} size="small" sx={{ bgcolor: '#f5efe2', color: '#17323c', fontWeight: 800 }} />
                                </Stack>
                              </Stack>
                            ))}
                          </Stack>
                        </>
                      ) : (
                        <Alert severity="info" sx={{ borderRadius: 2.8 }}>
                          L’historique Asset Scanner n’est pas encore disponible pour cet actif.
                        </Alert>
                      )}
                    </Stack>
                  </Paper>

                  <Stack direction="row" spacing={0.8} flexWrap="wrap" useFlexGap>
                    {assetScannerSnapshot?.detectedPattern ? (
                      <Chip label={`Pattern ${getPatternTitle(assetScannerSnapshot.detectedPattern)}`} sx={{ bgcolor: '#eef4f6', color: '#17323c', fontWeight: 800 }} />
                    ) : null}
                    {Number.isFinite(Number(assetScannerSnapshot?.relativeStrengthScore)) ? (
                      <Chip label={`Relative strength ${formatAssetMetric(assetScannerSnapshot.relativeStrengthScore)}`} sx={{ bgcolor: '#eef4f6', color: '#17323c', fontWeight: 800 }} />
                    ) : null}
                    {Number.isFinite(Number(assetScannerSnapshot?.volumeAccelerationScore)) ? (
                      <Chip label={`Volume accel ${formatAssetMetric(assetScannerSnapshot.volumeAccelerationScore)}`} sx={{ bgcolor: 'rgba(31,122,77,0.12)', color: '#1f7a4d', fontWeight: 800 }} />
                    ) : null}
                    {Number.isFinite(Number(assetScannerSnapshot?.regimeCompatibilityScore)) ? (
                      <Chip label={`Régime ${formatAssetMetric(assetScannerSnapshot.regimeCompatibilityScore)}`} sx={{ bgcolor: '#f5efe2', color: '#17323c', fontWeight: 800 }} />
                    ) : null}
                    {Number.isFinite(Number(assetScannerSnapshot?.volume)) ? (
                      <Chip label={`Volume 24h ${formatAssetVolume(assetScannerSnapshot.volume)}`} sx={{ bgcolor: '#f5efe2', color: '#17323c', fontWeight: 800 }} />
                    ) : null}
                    {Number.isFinite(Number(assetScannerSnapshot?.fakeoutRisk)) ? (
                      <Chip label={`Fakeout ${formatAssetMetric(assetScannerSnapshot.fakeoutRisk)}%`} sx={{ bgcolor: 'rgba(170,61,51,0.12)', color: '#aa3d33', fontWeight: 800 }} />
                    ) : null}
                  </Stack>

                  <Paper elevation={0} sx={{ p: 1.6, borderRadius: 3.2, background: '#fffdf8', border: '1px solid rgba(16,37,43,0.06)' }}>
                    <Typography variant="subtitle2" sx={{ color: '#17323c', fontWeight: 900, mb: 0.7 }}>Lecture synthétique</Typography>
                    <Typography variant="body2" sx={{ color: '#5d6e75', lineHeight: 1.75 }}>{assetScannerNarrative}</Typography>
                  </Paper>
                </>
              ) : null}
            </Stack>
            </Paper>
          </DetailTabPanel>

          <DetailTabPanel value="analysis" activeValue={activeDetailTab}>
            <Paper component="section" aria-label="Analyse détaillée" elevation={0} sx={{ p: 2.2, borderRadius: 4, border: '1px solid rgba(16,37,43,0.08)', background: '#fcfbf7' }}>
              <Stack spacing={1.5}>
                <Typography variant="h5" sx={{ color: '#17323c', fontWeight: 900 }}>Analyse détaillée</Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }, gap: 1.2 }}>
                  {detailSections.map((section) => (
                    <Paper key={section.title} elevation={0} sx={{ p: 1.7, borderRadius: 3.2, background: '#fff', border: '1px solid rgba(16,37,43,0.06)' }}>
                      <Typography variant="subtitle1" sx={{ color: '#17323c', fontWeight: 900, mb: 1 }}>{section.title}</Typography>
                      <Stack spacing={0.9}>
                        {section.items.map(([label, value]) => (
                          <Box key={`${section.title}-${label}`}>
                            <Typography variant="caption" sx={{ color: '#6b7a80', letterSpacing: '0.04em' }}>{label}</Typography>
                            <Typography variant="body2" sx={{ color: '#41535b', lineHeight: 1.7 }}>{value}</Typography>
                          </Box>
                        ))}
                      </Stack>
                    </Paper>
                  ))}
                </Box>
              </Stack>
            </Paper>
          </DetailTabPanel>

          <DetailTabPanel value="history" activeValue={activeDetailTab}>
            <Paper component="section" aria-label="Historique Pattern" elevation={0} sx={{ p: 2.2, borderRadius: 4, border: '1px solid rgba(16,37,43,0.08)' }}>
              <Typography variant="h5" sx={{ color: '#17323c', fontWeight: 900, mb: 1.4 }}>Historique Pattern</Typography>
              {loading ? (
                <SectionLoadingNotice
                  message={isInitialScreenLoading
                    ? 'Historique du pattern en cours de chargement.'
                    : `Actualisation de l’historique ${timeframe} en cours.`}
                />
              ) : null}
              <Stack spacing={1.1}>
                {(screen?.history || []).slice(0, 6).map((entry) => (
                  <Stack key={`${entry.symbol}-${entry.timeframe}-${entry.analysisTimestamp}`} direction="row" spacing={1.2} alignItems="center">
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#f3c65c', flexShrink: 0 }} />
                    <Typography variant="body2" sx={{ color: '#17323c', fontWeight: 700, minWidth: 64 }}>{formatHistoryDate(entry.analysisTimestamp)}</Typography>
                    <Typography variant="body2" sx={{ color: '#5d6e75' }}>{getPatternTitle(entry.patternDetected)}</Typography>
                  </Stack>
                ))}
                {screen && (!screen.history || screen.history.length === 0) ? (
                  <Alert severity="info">Aucun historique n’est encore disponible pour cette fenêtre.</Alert>
                ) : null}
              </Stack>
            </Paper>
          </DetailTabPanel>
        </Stack>
      </Container>
    </Box>
  );
};

export default PatternScannerDetailPage;
